import React, { useContext, useEffect, useState } from 'react';
import { Card, CardBody, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { EmailService } from './service';
import { ErrorToast } from '../../shared/toasters/toasters';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useTranslation } from 'react-i18next';
import { formatDateToYYYYMMDDLocal } from '../../shared/utils/functions';
import { EnumsContext } from '../../context/EnumsContext';
import Pagination from '../../Common/Pagination';

const EmailList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const enumsContext = useContext(EnumsContext);
  const [list, setList] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [receiver, setReceiver] = useState('');
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [statusOptions, setStatusOptions] = useState<any[]>([]);
  const [typeOptions, setTypeOptions] = useState<any[]>([]);

  useEffect(() => {
    if (enumsContext?.enums?.emailStatus) {
      setStatusOptions(
        enumsContext.enums.emailStatus.map((val: string) => ({
          label: t(`enums.emailStatus.${val}`),
          value: val,
        }))
      );
    }
  }, [enumsContext?.enums?.emailStatus]);

  useEffect(() => {
    if (enumsContext?.enums?.emailType) {
      setTypeOptions(
        enumsContext.enums.emailType.map((val: string) => ({
          label: t(`enums.emailType.${val}`),
          value: val,
        }))
      );
    }
  }, [enumsContext?.enums?.emailType]);

  const entriesPerPageOptions = [
    { label: '5', value: 5 },
    { label: '10', value: 10 },
    { label: '20', value: 20 },
    { label: '30', value: 30 },
    { label: '40', value: 40 },
  ];

  const handleEntriesPerPageChange = (selectedOption: any) => {
    setEntriesPerPage(selectedOption ? selectedOption.value : 10);
    setCurrentPage(1);
  };

  const handleStatusChange = (selectedOption: any) => {
    setSelectedStatus(selectedOption ? selectedOption.value : null);
    setCurrentPage(1);
  };

  const handleTypeChange = (selectedOption: any) => {
    setSelectedType(selectedOption ? selectedOption.value : null);
    setCurrentPage(1);
  };

  const handleReceiverChange = (e: any) => {
    setReceiver(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (pageNumber: number) => {    setCurrentPage(pageNumber);
  };

  const handleExport = async () => {
    try {
      const response = await EmailService.exportEmails({
        status: selectedStatus,
        type: selectedType,
        receiver: receiver || null,
        fromDate: fromDate ? formatDateToYYYYMMDDLocal(fromDate) : null,
        toDate: toDate ? formatDateToYYYYMMDDLocal(toDate) : null,
      });
      const url = URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'emails.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      ErrorToast(error);
    }
  };

  const fetchData = async () => {
    try {
      const response = await EmailService.getAll({
        page: currentPage,
        perPage: entriesPerPage,
        status: selectedStatus,
        type: selectedType,
        receiver: receiver || null,
        fromDate: fromDate ? formatDateToYYYYMMDDLocal(fromDate) : null,
        toDate: toDate ? formatDateToYYYYMMDDLocal(toDate) : null,
      });
      const data = response.data;
      setList(data);
      setTotalPages(response.totalPages);
    } catch (error) {
      ErrorToast(error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, entriesPerPage, selectedStatus, selectedType, receiver, fromDate, toDate]);

  return (
    <React.Fragment>
      <Col>
        <Card>
          <CardBody className="card-body">
            <div className="d-sm-flex align-items-center mb-3">
              <ul className="list-inline me-auto my-1">
                <li className="list-inline-item">
                  <Select
                    placeholder={t('email.entriesPerPage')}
                    options={entriesPerPageOptions}
                    onChange={handleEntriesPerPageChange}
                    defaultValue={entriesPerPageOptions.find(
                      (option) => option.value === entriesPerPage
                    )}
                    classNamePrefix="react-select"
                  />
                </li>
              </ul>
              <ul className="list-inline ms-auto my-1 mt-3 d-flex">
                <li className="list-inline-item">
                  <Select
                    placeholder={t('email.selectStatus')}
                    options={statusOptions}
                    onChange={handleStatusChange}
                    isClearable
                    classNamePrefix="react-select"
                  />
                </li>
                <li className="list-inline-item">
                  <Select
                    placeholder={t('email.selectType')}
                    options={typeOptions}
                    onChange={handleTypeChange}
                    isClearable
                    classNamePrefix="react-select"
                  />
                </li>
                <li className="list-inline-item">
                  <input
                    type="search"
                    className="form-control"
                    placeholder={t('email.receiverPlaceholder')}
                    onChange={handleReceiverChange}
                  />
                </li>
                <li className="list-inline-item">
                  <DatePicker
                    selected={fromDate}
                    onChange={(date: Date | null) => { setFromDate(date); setCurrentPage(1); }}
                    placeholderText={t('email.fromDate')}
                    className="form-control"
                    dateFormat="dd.MM.yyyy"
                  />
                </li>
                <li className="list-inline-item">
                  <DatePicker
                    selected={toDate}
                    onChange={(date: Date | null) => { setToDate(date); setCurrentPage(1); }}
                    placeholderText={t('email.toDate')}
                    className="form-control"
                    dateFormat="dd.MM.yyyy"
                  />
                </li>
                <li className="list-inline-item">
                  <button className="btn btn-success" onClick={handleExport}>
                    <i className="ti ti-file-export f-18"></i> {t('email.export')}
                  </button>
                </li>
              </ul>
            </div>
            <div className="table-responsive">
              <table className="table table-hover sticky-actions" id="pc-dt-email-list">
                <thead>
                  <tr>
                    <th>{t('email.createdAt')}</th>
                    <th>{t('email.sendAt')}</th>
                    <th>{t('email.status')}</th>
                    <th>{t('email.type')}</th>
                    <th>{t('email.receiver')}</th>
                    <th className="sticky-col">{t('email.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((item: any, index: number) => (
                    <tr key={index}>
                      <td>{new Date(item.createdAt).toLocaleDateString()}</td>
                      <td>{item.sendAt ? new Date(item.sendAt).toLocaleDateString() : 'N/A'}</td>
                      <td>{item.status}</td>
                      <td>{item.type}</td>
                      <td className="wrap-col">{item.receiver}</td>
                      <td className="text-nowrap sticky-col">
                        <button
                          className="btn btn-sm btn-info"
                          onClick={() => navigate(`/emails/view/${item.id}`)}
                          title={t('email.view')}
                        >
                          <i className="ti ti-eye"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              previousLabel={t('email.previous')}
              nextLabel={t('email.next')}
            />
          </CardBody>
        </Card>
      </Col>
    </React.Fragment>
  );
};

export default EmailList;

